import { useToast } from '@/hooks/use-toast'
import { assuredPeopleApi } from '@/rest/modules/assuredPeople-api'
import { AssuredPerson } from '@/domain/assured/Assured'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiResponse } from '@/domain'

export const assuredPeopleKeys = {
  all: ['assuredPeople'] as const,
  lists: () => [...assuredPeopleKeys.all, 'list'] as const,
  details: () => [...assuredPeopleKeys.all, 'detail'] as const,
  detail: (id: string) => [...assuredPeopleKeys.details(), id] as const,
  byContract: (contractId: string) => [...assuredPeopleKeys.all, 'contract', contractId] as const,
  verify: (contractIds: string[]) => [...assuredPeopleKeys.all, 'verify', contractIds] as const,
}

export const useAssuredPeopleByContract = (contractId: string) => {
  return useQuery({
    queryKey: assuredPeopleKeys.byContract(contractId),
    queryFn: () => assuredPeopleApi.getAssuredPeopleByContract(contractId),
    enabled: !!contractId,
    staleTime: 100,
  })
}

export const useAssuredPerson = (id: string) => {
  return useQuery({
    queryKey: assuredPeopleKeys.detail(id),
    queryFn: () => assuredPeopleApi.getAssuredPerson(id),
    enabled: !!id,
  })
}

export const useCreateAssuredPerson = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: Partial<AssuredPerson>) => assuredPeopleApi.createAssuredPerson(data),
    onSuccess: () => {
      toast({
        title: 'Éxito',
        description: 'Abonado creado correctamente',
      })
      queryClient.invalidateQueries({ queryKey: assuredPeopleKeys.all })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'No se pudo crear el abonado',
        variant: 'destructive',
      })
      console.error('Error creating assured person:', error)
    },
  })
}

export const useUpdateAssuredPerson = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AssuredPerson> }) =>
      assuredPeopleApi.updateAssuredPerson(id, data),
    onSuccess: () => {
      toast({
        title: 'Éxito',
        description: 'Abonado actualizado correctamente',
      })
      queryClient.invalidateQueries({ queryKey: assuredPeopleKeys.all })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.error || response.message || 'No se pudo actualizar el abonado',
        variant: 'destructive',
      })
      console.error('Error updating assured person:', response)
    },
  })
}

export const useDeleteAssuredPerson = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: string) => assuredPeopleApi.deleteAssuredPerson(id),
    onSuccess: () => {
      toast({
        title: 'Éxito',
        description: 'Abonado eliminado correctamente',
      })
      queryClient.invalidateQueries({ queryKey: assuredPeopleKeys.all })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el abonado',
        variant: 'destructive',
      })
      console.error('Error deleting assured person:', error)
    },
  })
}

export const useAssuredPeopleVerify = (contractIds: string[]) => {
  return useQuery({
    queryKey: assuredPeopleKeys.verify(contractIds),
    queryFn: () => assuredPeopleApi.verifyAssuredPeople(contractIds),
    enabled: contractIds.length > 0,
    staleTime: 100,
  })
}
